import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "../../../lib/api";
import { rupiah } from "../../../lib/format";
import { safeStorage } from "../../../lib/token";
import { createAlbum, uploadCover } from "../../../lib/hostApi";
import { resizeCover } from "../../../lib/image";
import { FILTERS, FILTER_LABELS, FILM_KEYS, SETTINGS_ENUMS } from "../../../lib/theme";
import { InviteCard } from "../../../components/InviteCard";
import {
  DURATIONS, EVENT_KEYS, EVENT_PHOTO, FORMAT_LABELS, MODE_KEYS, PHOTOS_PER_GUEST, TIERS,
  TRIAL, defaultDraft, defaultStart, isSuggestedTitle, parseTrial, stepValid, titleSuggestions, toBody, todayISO, visibleSteps, type Draft, type StepId,
} from "../../../lib/wizard";
import { useT, type Key } from "../../../lib/i18n";
import { GUEST_TIERS } from "../../../../../worker/pricing";

export const Route = createFileRoute("/host/albums/new")({
  component: NewAlbumWizard,
  validateSearch: (s: Record<string, unknown>): { trial?: true } => (parseTrial(s.trial) ? { trial: true } : {}),
});

const KEY = "album-draft";
const store = safeStorage("session");
const PORTRAIT = "/photos/sample-portrait.webp";

const HEAD: Record<StepId, [Key, Key]> = {
  event: ["host.wizard.head.event.title", "host.wizard.head.event.sub"],
  title: ["host.wizard.head.title.title", "host.wizard.head.title.sub"],
  camera: ["host.wizard.head.camera.title", "host.wizard.head.camera.sub"],
  filter: ["host.wizard.head.filter.title", "host.wizard.head.filter.sub"],
  format: ["host.wizard.head.format.title", "host.wizard.head.format.sub"],
  card: ["host.wizard.head.card.title", "host.wizard.head.card.sub"],
  reveal: ["host.wizard.head.reveal.title", "host.wizard.head.reveal.sub"],
  visibility: ["host.wizard.head.visibility.title", "host.wizard.head.visibility.sub"],
  interaction: ["host.wizard.head.interaction.title", "host.wizard.head.interaction.sub"],
  date: ["host.wizard.head.date.title", "host.wizard.head.date.sub"],
  guests: ["host.wizard.head.guests.title", "host.wizard.head.guests.sub"],
};

function load(): Draft {
  try {
    const raw = store.get(KEY);
    const d = raw ? { ...defaultDraft(), ...(JSON.parse(raw) as Partial<Draft>) } : defaultDraft();
    if (!TIERS.includes(d.guests)) d.guests = defaultDraft().guests;
    if (!(PHOTOS_PER_GUEST as readonly number[]).includes(d.photos_per_guest)) d.photos_per_guest = defaultDraft().photos_per_guest;
    if (!d.date || d.date < todayISO()) Object.assign(d, defaultStart(new Date()));
    return d;
  } catch {
    return defaultDraft();
  }
}

/** Unix detik dari tanggal+jam draft; kalau belum lengkap pakai sekarang (hanya untuk pratinjau). */
const draftStart = (d: Draft) => {
  const ms = new Date(`${d.date}T${d.time}`).getTime();
  return Math.floor((Number.isFinite(ms) ? ms : Date.now()) / 1000);
};

function NewAlbumWizard() {
  const { t } = useT();
  const navigate = useNavigate();
  const trial = Route.useSearch().trial === true;
  const [draft, setDraft] = useState<Draft>(load);
  const [i, setI] = useState(0);
  const [cover, setCover] = useState<File | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const steps = visibleSteps(draft, trial);
  const idx = Math.min(i, steps.length - 1);
  const step = steps[idx];
  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  useEffect(() => {
    store.set(KEY, JSON.stringify(draft));
  }, [draft]);

  // File cover hanya di state (tidak ikut sessionStorage); object URL dibersihkan saat ganti/unmount.
  useEffect(() => {
    if (!cover) return setCoverUrl(null);
    const u = URL.createObjectURL(cover);
    setCoverUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [cover]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [idx]);

  const create = useMutation({
    mutationFn: async () => {
      const album = await createAlbum(toBody(draft, trial));
      let coverFailed = false;
      if (cover) {
        try {
          await uploadCover(String(album.id), await resizeCover(cover));
        } catch (e) {
          console.warn("upload cover gagal", e);
          coverFailed = true;
        }
      }
      return { album, coverFailed };
    },
    onSuccess: ({ album, coverFailed }) => {
      store.remove(KEY);
      const id = String(album.id);
      if (album.status === "pending_payment") {
        // Halaman bayar tidak punya unggah cover; beri tahu sekali sebelum pindah.
        if (coverFailed) alert(t("host.wizard.cover_upload_failed_alert"));
        navigate({ to: "/host/albums/$id/pay", params: { id } });
      } else if (coverFailed) navigate({ to: "/host/albums/$id/settings", params: { id }, search: { cover: "failed" } });
      else navigate({ to: "/host/albums/$id", params: { id } });
    },
  });

  const last = idx >= steps.length - 1;
  const valid = stepValid(step, draft);
  const back = () => (idx === 0 ? navigate({ to: "/host" }) : setI(idx - 1));
  const [title, sub] = HEAD[step];
  const minTier = GUEST_TIERS[TIERS[0] as keyof typeof GUEST_TIERS];

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            aria-label={t("common.back")}
            onClick={back}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-sand bg-white text-lg text-ink"
          >
            ←
          </button>
          <span className="truncate rounded-full bg-white px-3 py-1.5 text-xs font-medium text-cocoa ring-1 ring-sand/60">
            {trial
              ? t("host.wizard.trial_badge", { guests: TRIAL.guests, photos: TRIAL.photos_per_guest, days: TRIAL.days })
              : t("host.wizard.starting_from", { price: rupiah(minTier) })}
          </span>
        </div>
        <div className="h-1 w-full rounded-full bg-sand/40">
          <div className="h-1 rounded-full bg-ink transition-all duration-300" style={{ width: `${((idx + 1) / steps.length) * 100}%` }} />
        </div>
      </div>

      <div className="space-y-2">
        <h1 className="font-serif text-3xl leading-tight text-ink">{t(title)}</h1>
        <p className="text-neutral-600">{t(sub)}</p>
        {(step === "card" || step === "date") && (
          <span className="inline-block rounded-full bg-cream px-3 py-1 text-xs text-neutral-600 ring-1 ring-sand/60">
            {t("host.wizard.editable_later")}
          </span>
        )}
      </div>

      <Step key={step} id={step} d={draft} patch={patch} trial={trial} cover={coverUrl} setCover={setCover} />

      <div className="sticky bottom-0 -mx-4 -mb-6 space-y-3 bg-cream px-4 pt-3 pb-6 shadow-[0_-12px_16px_-12px_rgba(22,21,20,0.12)]">
        {create.error && (
          <p className="text-sm text-red-600" role="alert">
            {create.error instanceof ApiError ? create.error.message : t("host.wizard.create_failed")}
          </p>
        )}
        <div className="flex gap-3">
          <button type="button" onClick={back} disabled={create.isPending} className="btn-secondary">
            {t("common.back")}
          </button>
          <button
            type="button"
            onClick={() => (last ? create.mutate() : setI(idx + 1))}
            disabled={!valid || create.isPending}
            className="btn-primary flex-1"
          >
            {create.isPending ? t("host.wizard.saving") : last ? t("host.wizard.create") : t("host.wizard.next")}
          </button>
        </div>
      </div>
    </div>
  );
}

type P = { d: Draft; patch: (p: Partial<Draft>) => void };

function Step({
  id, d, patch, trial, cover, setCover,
}: P & { id: StepId; trial: boolean; cover: string | null; setCover: (f: File | null) => void }) {
  const { t } = useT();
  switch (id) {
    case "event":
      return (
        <div className="grid grid-cols-2 gap-3">
          {SETTINGS_ENUMS.event_type.map((k) => (
            <button
              type="button"
              key={k}
              aria-pressed={d.event_type === k}
              onClick={() => {
                const keep = d.title.trim() !== "" && !isSuggestedTitle(d.event_type, d.title);
                patch({ event_type: k, title: keep ? d.title : titleSuggestions(k)[0] });
              }}
              className={`relative aspect-[4/5] overflow-hidden rounded-3xl text-left transition active:scale-[0.98] ${d.event_type === k ? "ring-2 ring-ink ring-offset-2 ring-offset-cream" : ""}`}
            >
              <img src={EVENT_PHOTO[k]} alt="" className="absolute inset-0 h-full w-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent px-4 pb-3 pt-10 font-serif text-lg text-white">
                {t(EVENT_KEYS[k])}
              </span>
              {d.event_type === k && <Tick />}
            </button>
          ))}
        </div>
      );
    case "title":
      return (
        <div className="space-y-4">
          <input
            className="field font-serif text-xl text-ink"
            autoFocus
            maxLength={80}
            placeholder={t("host.wizard.title.placeholder")}
            value={d.title}
            onChange={(e) => patch({ title: e.target.value })}
          />
          <div>
            <p className="mb-2 text-xs font-semibold tracking-widest text-neutral-500">{t("host.wizard.title.suggestions_label")}</p>
            <div className="flex flex-wrap gap-2">
              {titleSuggestions(d.event_type).map((s) => (
                <Pill key={s} active={d.title === s} onClick={() => patch({ title: s })}>
                  {s}
                </Pill>
              ))}
            </div>
          </div>
        </div>
      );
    case "camera":
      return (
        <Cards
          cols={2}
          items={[
            {
              key: "analogue",
              label: t("host.wizard.camera.analogue_label"),
              hint: t("host.wizard.camera.analogue_hint"),
              img: PORTRAIT,
              imgStyle: { filter: FILTERS.portra800 },
            },
            {
              key: "instant",
              label: t("host.wizard.camera.instant_label"),
              hint: t("host.wizard.camera.instant_hint"),
              img: PORTRAIT,
              frame: "polaroid",
            },
          ]}
          value={d.camera ?? ""}
          onPick={(k) => patch({ camera: k as Draft["camera"], filter_preset: k === "analogue" ? d.filter_preset : null })}
        />
      );
    case "filter":
      return (
        <Cards
          cols={2}
          items={FILM_KEYS.map((k) => ({ key: k, label: FILTER_LABELS[k], img: PORTRAIT, imgStyle: { filter: FILTERS[k] } }))}
          value={d.filter_preset ?? ""}
          onPick={(k) => patch({ filter_preset: k })}
        />
      );
    case "format":
      return (
        <Cards
          cols={3}
          items={SETTINGS_ENUMS.instant_format.map((k) => ({
            key: k,
            label: FORMAT_LABELS[k],
            hint: { mini: t("host.wizard.format.hint_mini"), square: "1:1", wide: "3:2" }[k],
            img: PORTRAIT,
            frame: "polaroid" as const,
            imgStyle: { aspectRatio: { mini: "2/3", square: "1/1", wide: "3/2" }[k] },
          }))}
          value={d.instant_format}
          onPick={(k) => patch({ instant_format: k as Draft["instant_format"] })}
        />
      );
    case "card":
      return <CardStep d={d} patch={patch} trial={trial} cover={cover} setCover={setCover} />;
    case "reveal":
      return (
        <div className="space-y-4">
          {(
            [
              ["immediate", t(MODE_KEYS.immediate), t("host.wizard.reveal.immediate_hint"), "party-1", "none"],
              ["after_event", t(MODE_KEYS.after_event), t("host.wizard.reveal.after_event_hint"), "party-2", "blur(6px)"],
              [
                "delayed",
                t("host.wizard.reveal.delayed_label"),
                t("host.wizard.reveal.delayed_hint"),
                "party-3",
                "blur(10px) grayscale(0.4)",
              ],
            ] as const
          ).map(([k, label, hint, img, filter]) => (
            <Option
              key={k}
              active={d.gallery_mode === k}
              onClick={() => patch({ gallery_mode: k })}
              label={label}
              hint={hint}
              art={<img src={`/photos/${img}.webp`} alt="" className="h-full w-full scale-110 object-cover" style={{ filter }} />}
            />
          ))}
          {d.gallery_mode === "delayed" && (
            <div className="card space-y-3">
              <p className="text-sm font-medium text-ink">{t("host.wizard.reveal.delay_label")}</p>
              <div className="flex flex-wrap gap-2">
                {[30, 60, 180, 720, 1440].map((m) => (
                  <Pill key={m} active={d.gallery_delay_min === m} onClick={() => patch({ gallery_delay_min: m })}>
                    {m < 60 ? t("units.minute", { count: m }) : m < 1440 ? t("units.hour", { count: m / 60 }) : t("units.day", { count: 1 })}
                  </Pill>
                ))}
              </div>
              <label className="block text-sm text-neutral-600">
                {t("host.wizard.reveal.custom_minutes_label")}
                <input
                  type="number"
                  min={1}
                  className="field mt-1"
                  value={d.gallery_delay_min}
                  onChange={(e) => patch({ gallery_delay_min: Number(e.target.value) })}
                />
              </label>
            </div>
          )}
        </div>
      );
    case "visibility":
      return (
        <div className="space-y-3">
          <Option
            active={d.visibility === "all"}
            onClick={() => patch({ visibility: "all" })}
            label={t("host.wizard.visibility.all_label")}
            hint={t("host.wizard.visibility.all_hint")}
            art={
              <div className="grid h-full grid-cols-2 grid-rows-2 gap-0.5">
                {["party-1", "party-2", "party-3", "wedding"].map((p) => (
                  <img key={p} src={`/photos/${p}.webp`} alt="" className="h-full w-full object-cover" />
                ))}
              </div>
            }
          />
          <Option
            active={d.visibility === "own"}
            onClick={() => patch({ visibility: "own" })}
            label={t("host.wizard.visibility.own_label")}
            hint={t("host.wizard.visibility.own_hint")}
            art={<img src={PORTRAIT} alt="" className="h-full w-full object-cover" />}
          />
        </div>
      );
    case "interaction":
      return (
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-3xl">
            <img src="/photos/party-2.webp" alt="" className="aspect-[4/3] w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 flex gap-2 bg-gradient-to-t from-ink/70 to-transparent p-4 pt-10 text-sm text-white">
              {d.likes && <span className="rounded-full bg-white/20 px-3 py-1 backdrop-blur">♥ 12</span>}
              {d.comments && (
                <span className="rounded-full bg-white/20 px-3 py-1 backdrop-blur">{t("host.wizard.interaction.sample_comments")}</span>
              )}
              {!d.likes && !d.comments && <span className="text-white/80">{t("host.wizard.interaction.no_extras")}</span>}
            </div>
          </div>
          <div className="card divide-y divide-sand/40 p-0">
            <Toggle
              label={t("host.wizard.interaction.likes_label")}
              hint={t("host.wizard.interaction.likes_hint")}
              checked={d.likes}
              onChange={(v) => patch({ likes: v })}
            />
            <Toggle
              label={t("host.wizard.interaction.comments_label")}
              hint={t("host.wizard.interaction.comments_hint")}
              checked={d.comments}
              onChange={(v) => patch({ comments: v })}
            />
          </div>
        </div>
      );
    case "date":
      return <EventDate d={d} patch={patch} />;
    case "guests":
      return <Guests d={d} patch={patch} />;
  }
}

function CardStep({ d, patch, trial, cover, setCover }: P & { trial: boolean; cover: string | null; setCover: (f: File | null) => void }) {
  const { t } = useT();
  const input = useRef<HTMLInputElement>(null);
  const start = draftStart(d);
  return (
    <div className="space-y-4">
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) setCover(f);
          e.target.value = "";
        }}
      />
      <div className="flex gap-3">
        <button type="button" className="btn-secondary flex-1 bg-white" onClick={() => input.current?.click()}>
          {cover ? t("host.wizard.card.change_cover") : t("host.wizard.card.upload_cover")}
        </button>
        {cover && (
          <button type="button" className="btn-secondary" onClick={() => setCover(null)}>
            {t("host.wizard.card.remove")}
          </button>
        )}
      </div>
      {!cover && (
        <p className="text-center text-xs text-neutral-500">
          {t("host.wizard.card.no_cover_hint", { type: t(EVENT_KEYS[d.event_type]).toLowerCase() })}
        </p>
      )}
      <label className="block text-sm text-neutral-600">
        {t("host.wizard.card.message_label")}
        <textarea
          className="field mt-1 resize-none"
          rows={3}
          maxLength={280}
          placeholder={t("host.wizard.card.message_placeholder")}
          value={d.host_message}
          onChange={(e) => patch({ host_message: e.target.value })}
        />
      </label>
      <div className="mx-auto max-w-[300px]">
        <InviteCard
          title={d.title.trim() || t("host.wizard.card.title_placeholder")}
          eventType={d.event_type}
          cover={cover}
          eventStart={start}
          eventEnd={start + d.duration_min * 60}
          photosPerGuest={trial ? TRIAL.photos_per_guest : d.photos_per_guest}
          now={Date.now() / 1000}
        >
          <input className="field" disabled placeholder={t("host.wizard.card.name_placeholder")} />
          <div className="btn-primary pointer-events-none flex w-full opacity-60">{t("host.wizard.card.enter_album")}</div>
        </InviteCard>
      </div>
    </div>
  );
}

function EventDate({ d, patch }: P) {
  const { t } = useT();
  return (
    <div className="space-y-5">
      <div className="card space-y-4">
        <label className="block text-sm text-neutral-600">
          {t("host.wizard.date.date_label")}
          <input type="date" min={todayISO()} className="field mt-1" value={d.date} onChange={(e) => patch({ date: e.target.value })} />
        </label>
        <label className="block text-sm text-neutral-600">
          {t("host.wizard.date.time_label")}
          <input type="time" className="field mt-1" value={d.time} onChange={(e) => patch({ time: e.target.value })} />
        </label>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold tracking-widest text-neutral-500">{t("host.wizard.date.duration_label")}</p>
        <div className="flex flex-wrap gap-2">
          {DURATIONS.map((x) => (
            <Pill key={x.min} active={d.duration_min === x.min} onClick={() => patch({ duration_min: x.min })}>
              {t(x.key)}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  );
}

function Guests({ d, patch }: P) {
  const { t } = useT();
  const idx = Math.max(0, TIERS.indexOf(d.guests));
  const price = GUEST_TIERS[d.guests as keyof typeof GUEST_TIERS];
  return (
    <div className="space-y-6">
      <div className="card space-y-5">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm text-neutral-500">{t("host.wizard.guests.up_to_label")}</p>
            <p className="font-serif text-3xl text-ink">{t("host.wizard.guests.count", { count: d.guests })}</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-emerald-700">{rupiah(price)}</p>
            <p className="text-xs text-neutral-500">{t("host.wizard.guests.upgrade_hint")}</p>
          </div>
        </div>
        <input
          type="range"
          min={0}
          max={TIERS.length - 1}
          step={1}
          value={idx}
          onChange={(e) => patch({ guests: TIERS[Number(e.target.value)] })}
          className="w-full accent-ink"
          aria-label={t("host.wizard.guests.slider_aria")}
          aria-valuetext={t("host.wizard.guests.count", { count: d.guests })}
        />
        <div className="flex justify-between text-xs text-neutral-500">
          <span>{t("host.wizard.guests.count", { count: TIERS[0] })}</span>
          <span>{t("host.wizard.guests.count", { count: TIERS[TIERS.length - 1] })}</span>
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold tracking-widest text-neutral-500">{t("host.wizard.guests.photos_per_guest_label")}</p>
        <div className="grid grid-cols-5 gap-2">
          {PHOTOS_PER_GUEST.map((n) => (
            <Pill key={n} active={d.photos_per_guest === n} onClick={() => patch({ photos_per_guest: n })}>
              {n}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  );
}

type Card = { key: string; label: string; hint?: string; img?: string; imgStyle?: CSSProperties; frame?: "polaroid" };
function Cards({ items, value, onPick, cols }: { items: Card[]; value: string; onPick: (k: string) => void; cols: 2 | 3 }) {
  return (
    <div className={`grid gap-3 ${cols === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
      {items.map((it) => {
        const on = value === it.key;
        return (
          <button
            type="button"
            key={it.key}
            onClick={() => onPick(it.key)}
            aria-pressed={on}
            className={`relative flex flex-col rounded-3xl border bg-white p-2 text-left transition active:scale-[0.98] ${on ? "border-ink ring-2 ring-ink" : "border-sand/60"}`}
          >
            {it.img &&
              (it.frame === "polaroid" ? (
                <span className="flex aspect-[4/5] items-center justify-center rounded-2xl bg-cream p-3">
                  <span className="block w-full bg-white p-1.5 pb-5 shadow-md">
                    <img src={it.img} alt="" className="aspect-square w-full object-cover" style={it.imgStyle} />
                  </span>
                </span>
              ) : (
                <span className="block aspect-[4/5] overflow-hidden rounded-2xl">
                  <img src={it.img} alt="" className="h-full w-full scale-110 object-cover" style={it.imgStyle} />
                </span>
              ))}
            <span className="block px-1.5 pt-2 font-medium leading-tight text-ink">{it.label}</span>
            {it.hint && <span className="block px-1.5 pb-1 text-xs text-neutral-500">{it.hint}</span>}
            {on && <Tick />}
          </button>
        );
      })}
    </div>
  );
}

function Option({ active, onClick, label, hint, art }: { active: boolean; onClick: () => void; label: string; hint: string; art?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`relative flex w-full items-center gap-4 rounded-3xl border bg-white p-3 text-left transition active:scale-[0.99] ${active ? "border-ink ring-2 ring-ink" : "border-sand/60"}`}
    >
      {art && <span className="block h-20 w-20 shrink-0 overflow-hidden rounded-2xl">{art}</span>}
      <span className="min-w-0 pr-6">
        <span className="block font-medium text-ink">{label}</span>
        <span className="block text-sm text-neutral-500">{hint}</span>
      </span>
      {active && <Tick />}
    </button>
  );
}

const Tick = () => (
  <span aria-hidden="true" className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs text-cream">✓</span>
);

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition ${active ? "border-ink bg-ink text-cream" : "border-sand bg-white text-cocoa"}`}
    >
      {children}
    </button>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 p-4">
      <span>
        <span className="block font-medium text-ink">{label}</span>
        <span className="block text-sm text-neutral-500">{hint}</span>
      </span>
      <input type="checkbox" role="switch" className="peer sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="relative h-7 w-12 shrink-0 rounded-full bg-sand/60 transition after:absolute after:left-1 after:top-1 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition peer-checked:bg-ink peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-ink peer-focus-visible:ring-offset-2" />
    </label>
  );
}
